import { useEffect, useRef, useState } from "react";
import { Routes, Route, NavLink, useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutDashboard, Package, ClipboardList, Users, Settings, Tag, LogOut, Store,
  IndianRupee, ShoppingCart, TrendingUp, AlertTriangle, Plus, Trash2, Edit, Upload,
  ExternalLink, X, Sparkles, Boxes, Image as ImageIcon, Link2, Star, Factory,
  FolderTree, GripVertical, ChevronRight, CreditCard,
  Truck, Printer, RefreshCw, MapPin, MessageSquare, Search, Clock, CheckCircle2, Phone, FileText, LayoutGrid,
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiError, inr, uploadFiles } from "@/lib/api";
import { THEMES, BUSINESS_CATEGORIES, themeForCategory } from "@/lib/themes";
import { useAuth } from "@/context/AuthContext";

const navItems = [
  { to: "/vendor", icon: LayoutDashboard, label: "Dashboard", end: true },
  { to: "/vendor/products", icon: Package, label: "Products" },
  { to: "/vendor/categories", icon: FolderTree, label: "Categories" },
  { to: "/vendor/orders", icon: ClipboardList, label: "Orders" },
  { to: "/vendor/delivery", icon: Truck, label: "Delivery Partners" },
  { to: "/vendor/customers", icon: Users, label: "Customers" },
  { to: "/vendor/coupons", icon: Tag, label: "Coupons" },
  { to: "/vendor/settings", icon: Settings, label: "Settings" },
];

const orderBadge = {
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  confirmed: "bg-blue-100 text-blue-800 border-blue-200",
  packed: "bg-purple-100 text-purple-800 border-purple-200",
  dispatched: "bg-indigo-100 text-indigo-800 border-indigo-200",
  out_for_delivery: "bg-cyan-100 text-cyan-800 border-cyan-200",
  delivered: "bg-emerald-100 text-emerald-800 border-emerald-200",
  failed: "bg-rose-100 text-rose-800 border-rose-200",
  cancelled: "bg-rose-100 text-rose-800 border-rose-200",
  returned: "bg-orange-100 text-orange-800 border-orange-200",
};

const VFLOW = ["pending", "confirmed", "packed", "dispatched", "out_for_delivery", "delivered"];
const STATUS_LABEL = {
  pending: "Pending", confirmed: "Confirmed", packed: "Packed", dispatched: "Dispatched",
  out_for_delivery: "Out for Delivery", delivered: "Delivered",
  failed: "Failed", cancelled: "Cancelled", returned: "Returned",
};
const TAB_ORDER = ["all", ...VFLOW, "failed", "cancelled", "returned"];
const fmtDate = (s) => { try { return new Date(s).toLocaleString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return s; } };

export default function VendorPortal() {
  const { user, vendor, logout } = useAuth();
  const navigate = useNavigate();
  if (user && user.role !== "vendor_admin") return <Navigate to="/" />;
  return (
    <div className="min-h-screen bg-[#F8FAFC] text-slate-900 flex">
      <aside className="w-64 shrink-0 border-r border-slate-200 flex flex-col fixed h-screen bg-white">
        <div className="h-16 flex items-center gap-2 px-6 border-b border-slate-200 font-display font-bold text-lg">
          <div className="h-8 w-8 rounded-lg grid place-items-center text-white" style={{ backgroundColor: vendor?.branding?.theme_color || "#0F172A" }}><Store className="h-5 w-5" /></div>
          <span className="truncate">{vendor?.branding?.store_name || "My Store"}</span>
        </div>
        {user?.impersonating && <div className="bg-amber-50 border-b border-amber-200 text-amber-800 text-xs px-4 py-2 flex items-center gap-2"><AlertTriangle className="h-3.5 w-3.5" />Support mode</div>}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {navItems.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`vendor-nav-${n.label.toLowerCase()}`}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${isActive ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              <n.icon className="h-4 w-4" />{n.label}
            </NavLink>
          ))}
          {vendor?.slug && <a href={`/${vendor.slug}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-slate-600 hover:bg-slate-100" data-testid="view-storefront"><ExternalLink className="h-4 w-4" />View storefront</a>}
        </nav>
        <div className="p-3 border-t border-slate-200">
          <button onClick={() => { logout(); navigate(user?.impersonating ? "/admin" : "/login"); }} data-testid="vendor-logout" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-slate-600 hover:bg-slate-100 w-full">
            <LogOut className="h-4 w-4" />{user?.impersonating ? "Exit support" : "Sign out"}
          </button>
        </div>
      </aside>
      <main className="flex-1 ml-64 p-8">
        {vendor?.status === "pending" && <div className="mb-6 p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm flex items-center gap-2"><AlertTriangle className="h-4 w-4" />Your store is pending platform approval. Customers can't shop until it's approved.</div>}
        <Routes>
          <Route index element={<Dashboard />} />
          <Route path="products" element={<Products />} />
          <Route path="categories" element={<Categories />} />
          <Route path="orders" element={<Orders />} />
          <Route path="delivery" element={<DeliveryPartners />} />
          <Route path="customers" element={<Customers />} />
          <Route path="coupons" element={<VendorCoupons />} />
          <Route path="settings" element={<StoreSettings />} />
        </Routes>
      </main>
    </div>
  );
}

function Head({ title, sub, children }) {
  return (
    <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
      <div><h1 className="font-display text-3xl font-bold tracking-tight">{title}</h1>{sub && <p className="text-slate-500 mt-1">{sub}</p>}</div>
      {children}
    </div>
  );
}
function Card({ children, className = "", ...rest }) {
  return <div className={`bg-white rounded-xl border border-slate-200 shadow-sm ${className}`} {...rest}>{children}</div>;
}
function Stat({ icon: Icon, label, value, accent }) {
  return (
    <Card className="p-6">
      <div className="flex items-center justify-between"><span className="text-xs uppercase tracking-widest text-slate-400">{label}</span><Icon className={`h-5 w-5 ${accent}`} /></div>
      <div className="font-display text-3xl font-bold mt-3">{value}</div>
    </Card>
  );
}

function Dashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/vendor/dashboard").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <div className="text-slate-500">Loading…</div>;
  return (
    <>
      <Head title="Store Command Center" sub="Your performance overview" />
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Stat icon={IndianRupee} label="Revenue" value={inr(d.revenue)} accent="text-emerald-600" />
        <Stat icon={ShoppingCart} label="Orders" value={d.orders} accent="text-blue-600" />
        <Stat icon={TrendingUp} label="Avg Order Value" value={inr(d.aov)} accent="text-indigo-600" />
        <Stat icon={AlertTriangle} label="Low Stock" value={d.low_stock} accent="text-amber-600" />
      </div>
      <div className="grid lg:grid-cols-3 gap-6 mt-6">
        <Card className="p-6 lg:col-span-2">
          <h3 className="font-display font-semibold text-lg mb-4">Revenue over time</h3>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={d.revenue_series}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#94a3b8" tickFormatter={(v) => v?.slice(5)} />
              <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
              <Tooltip formatter={(v) => inr(v)} />
              <Line type="monotone" dataKey="revenue" stroke="#2563EB" strokeWidth={2.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </Card>
        <Card className="p-6">
          <h3 className="font-display font-semibold text-lg mb-4">Top products</h3>
          <div className="space-y-3">
            {d.top_products.length === 0 && <p className="text-slate-400 text-sm">No sales yet.</p>}
            {d.top_products.map((p, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <span className="text-slate-700 truncate max-w-[55%]">{p.name}</span>
                <span className="text-slate-500">{p.units} sold · {inr(p.revenue)}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>
      <Card className="p-6 mt-6" data-testid="revenue-by-status">
        <h3 className="font-display font-semibold text-lg mb-4">Revenue by status</h3>
        {(!d.status_breakdown || d.status_breakdown.length === 0) ? (
          <p className="text-slate-400 text-sm">No orders yet.</p>
        ) : (() => {
          const order = [...VFLOW, "failed", "cancelled", "returned"];
          const rows = [...d.status_breakdown].sort((a, b) => order.indexOf(a.status) - order.indexOf(b.status));
          const maxRev = Math.max(...rows.map((r) => r.revenue), 1);
          return (
            <div className="space-y-3">
              {rows.map((r) => (
                <div key={r.status} data-testid={`rbs-${r.status}`}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <div className="flex items-center gap-2"><Badge variant="outline" className={orderBadge[r.status]}>{STATUS_LABEL[r.status] || r.status}</Badge><span className="text-slate-400 text-xs">{r.count} order{r.count !== 1 ? "s" : ""}</span></div>
                    <span className="font-medium text-slate-700">{inr(r.revenue)}</span>
                  </div>
                  <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full bg-slate-800" style={{ width: `${(r.revenue / maxRev) * 100}%` }} /></div>
                </div>
              ))}
            </div>
          );
        })()}
      </Card>
    </>
  );
}

const emptyProduct = {
  name: "", short_description: "", long_description: "", category: "", subcategory: "",
  price: "", discount: 0, tax_rate: 0, stock: 0, low_stock_threshold: 5, status: "active",
  images: [], variants: [], packs: [], related_ids: [],
  seo: { title: "", description: "", keywords: "" }, manufacturer: { name: "", brand: "", country: "" },
};

const PRODUCT_TABS = [
  ["basic", "Basic", Package],
  ["pricing", "Pricing & Stock", IndianRupee],
  ["media", "Media", ImageIcon],
  ["variants", "Variants", Boxes],
  ["packs", "Packs", Package],
  ["related", "Related", Link2],
  ["seo", "SEO", Star],
  ["manufacturer", "Manufacturer", Factory],
];

function Products() {
  const [rows, setRows] = useState([]);
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [csvOpen, setCsvOpen] = useState(false);
  const [csv, setCsv] = useState("");

  const load = () => api.get("/vendor/products", { params: { q: q || undefined } }).then((r) => setRows(r.data));
  const loadCats = () => api.get("/vendor/categories").then((r) => setCats(r.data));
  useEffect(() => { load(); }, [q]);
  useEffect(() => { loadCats(); }, []);

  const openNew = () => { setEditing(null); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setOpen(true); };
  const del = async (id) => { await api.delete(`/vendor/products/${id}`); toast.success("Deleted"); load(); };
  const uploadCsv = async () => { try { const { data } = await api.post("/vendor/products/bulk", { csv }); toast.success(`${data.created} products imported`); setCsvOpen(false); setCsv(""); load(); } catch (e) { toast.error(apiError(e)); } };

  return (
    <>
      <Head title="Products & Inventory" sub="Manage your catalog">
        <div className="flex gap-2">
          <Dialog open={csvOpen} onOpenChange={setCsvOpen}>
            <DialogTrigger asChild><Button variant="outline" data-testid="csv-btn"><Upload className="h-4 w-4 mr-1" />CSV import</Button></DialogTrigger>
            <DialogContent><DialogHeader><DialogTitle>Bulk CSV import</DialogTitle></DialogHeader>
              <p className="text-sm text-slate-500">Columns: name, description, category, price, stock, tax_rate, image</p>
              <textarea value={csv} onChange={(e) => setCsv(e.target.value)} rows={6} placeholder="name,category,price,stock&#10;Blue Mug,Home,120,50" className="w-full border rounded-lg p-3 text-sm font-mono" data-testid="csv-input" />
              <DialogFooter><Button onClick={uploadCsv} data-testid="csv-upload">Import</Button></DialogFooter>
            </DialogContent>
          </Dialog>
          <Button onClick={openNew} className="bg-slate-900 hover:bg-slate-800" data-testid="new-product-btn"><Plus className="h-4 w-4 mr-1" />Add product</Button>
        </div>
      </Head>
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products…" className="max-w-md mb-6" data-testid="product-search" />
      <Card>
        <table className="w-full text-sm">
          <thead className="text-slate-400 text-xs uppercase tracking-wider border-b border-slate-200"><tr>{["Product", "Category", "Price", "Stock", "Options", "Status", ""].map((h) => <th key={h} className="text-left px-4 py-3 font-medium">{h}</th>)}</tr></thead>
          <tbody>
            {rows.map((p) => {
              const low = p.stock <= p.low_stock_threshold;
              return (
                <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`product-row-${p.id}`}>
                  <td className="px-4 py-3"><div className="flex items-center gap-3">
                    {p.images?.[0] ? <img src={p.images[0]} alt="" className="h-10 w-10 rounded-lg object-cover" /> : <div className="h-10 w-10 rounded-lg bg-slate-100" />}
                    <span className="font-medium">{p.name}</span></div></td>
                  <td className="px-4 py-3 text-slate-500">{p.category}{p.subcategory ? ` / ${p.subcategory}` : ""}</td>
                  <td className="px-4 py-3">{inr(p.price)}</td>
                  <td className="px-4 py-3"><span className={low ? "text-amber-600 font-medium" : ""}>{p.stock}{low && " ⚠"}</span></td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{(p.variants?.length || 0)} var · {(p.packs?.length || 0)} pk</td>
                  <td className="px-4 py-3"><Badge variant="outline" className="capitalize">{p.status}</Badge></td>
                  <td className="px-4 py-3"><div className="flex gap-1 justify-end">
                    <button onClick={() => openEdit(p)} className="p-1.5 text-slate-500 hover:text-slate-900" data-testid={`edit-${p.id}`}><Edit className="h-4 w-4" /></button>
                    <button onClick={() => del(p.id)} className="p-1.5 text-slate-500 hover:text-rose-600" data-testid={`delete-${p.id}`}><Trash2 className="h-4 w-4" /></button>
                  </div></td>
                </tr>
              );
            })}
            {rows.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">No products yet</td></tr>}
          </tbody>
        </table>
      </Card>

      {open && <ProductEditor open={open} setOpen={setOpen} editing={editing} cats={cats} allProducts={rows} onSaved={load} />}
    </>
  );
}

function ProductEditor({ open, setOpen, editing, cats, allProducts, onSaved }) {
  const [tab, setTab] = useState("basic");
  const [f, setF] = useState(editing ? { ...emptyProduct, ...editing, seo: editing.seo || emptyProduct.seo, manufacturer: editing.manufacturer || emptyProduct.manufacturer } : emptyProduct);
  const [aiLoading, setAiLoading] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const moveImage = (from, to) => setF((p) => {
    const arr = [...(p.images || [])];
    if (from < 0 || to < 0 || from >= arr.length || to >= arr.length) return p;
    const [m] = arr.splice(from, 1);
    arr.splice(to, 0, m);
    return { ...p, images: arr };
  });
  const parents = cats.filter((c) => !c.parent_id);
  const selectedParent = cats.find((c) => c.name === f.category && !c.parent_id);
  const subs = selectedParent ? cats.filter((c) => c.parent_id === selectedParent.id) : [];

  const handleMediaUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    setUploadingMedia(true);
    try { const urls = await uploadFiles(files); setF((p) => ({ ...p, images: [...(p.images || []), ...urls] })); toast.success(`${urls.length} image(s) uploaded`); }
    catch (err) { toast.error(apiError(err)); } finally { setUploadingMedia(false); e.target.value = ""; }
  };
  const handleVariantUpload = async (i, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try { const [url] = await uploadFiles([file]); const a = [...f.variants]; a[i] = { ...a[i], image: url }; set("variants", a); toast.success("Variant image added"); }
    catch (err) { toast.error(apiError(err)); } finally { e.target.value = ""; }
  };

  const aiGenerate = async () => {
    if (!f.name) { toast.error("Enter a product name first"); return; }
    setAiLoading(true);
    try {
      const { data } = await api.post("/vendor/ai/description", { name: f.name, category: f.category, keywords: f.seo?.keywords });
      setF((p) => ({ ...p, short_description: data.short_description || p.short_description, long_description: data.long_description || p.long_description }));
      toast.success("AI copy generated");
    } catch (e) { toast.error(apiError(e)); } finally { setAiLoading(false); }
  };

  const save = async () => {
    const payload = {
      ...f, price: Number(f.price) || 0, discount: Number(f.discount) || 0, tax_rate: Number(f.tax_rate) || 0,
      stock: Number(f.stock) || 0, low_stock_threshold: Number(f.low_stock_threshold) || 0,
      description: f.long_description || f.short_description || "",
      variants: (f.variants || []).map((v) => ({ ...v, price: Number(v.price) || 0, stock: Number(v.stock) || 0 })),
      packs: (f.packs || []).map((p) => ({ ...p, qty: Number(p.qty) || 1, price: Number(p.price) || 0 })),
    };
    if (!payload.name) { toast.error("Product name is required"); setTab("basic"); return; }
    try {
      if (editing) { await api.put(`/vendor/products/${editing.id}`, payload); toast.success("Product updated"); }
      else { await api.post("/vendor/products", payload); toast.success("Product created"); }
      setOpen(false); onSaved();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-4xl p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-4 border-b"><DialogTitle className="font-display text-2xl">{editing ? "Edit product" : "New product"}</DialogTitle></DialogHeader>
        <div className="flex" style={{ minHeight: 460 }}>
          <div className="w-52 shrink-0 border-r bg-slate-50 p-3 space-y-1">
            {PRODUCT_TABS.map(([id, label, Icon]) => (
              <button key={id} onClick={() => setTab(id)} data-testid={`ptab-${id}`}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm transition-colors ${tab === id ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-200"}`}>
                <Icon className="h-4 w-4" />{label}
              </button>
            ))}
          </div>
          <div className="flex-1 p-6 overflow-y-auto max-h-[60vh]">
            {tab === "basic" && (
              <div className="space-y-4">
                <div><Label>Product name</Label><Input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="pf-name" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Category</Label>
                    {parents.length > 0 ? (
                      <Select value={f.category} onValueChange={(v) => { set("category", v); set("subcategory", ""); }}><SelectTrigger data-testid="pf-category"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>{parents.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent></Select>
                    ) : <Input value={f.category} onChange={(e) => set("category", e.target.value)} placeholder="e.g. Home & Living" data-testid="pf-category" />}
                  </div>
                  <div><Label>Sub-category</Label>
                    {subs.length > 0 ? (
                      <Select value={f.subcategory} onValueChange={(v) => set("subcategory", v)}><SelectTrigger data-testid="pf-subcategory"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>{subs.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent></Select>
                    ) : <Input value={f.subcategory} onChange={(e) => set("subcategory", e.target.value)} placeholder="optional" data-testid="pf-subcategory" />}
                  </div>
                </div>
                <div><Label>Status</Label>
                  <Select value={f.status} onValueChange={(v) => set("status", v)}><SelectTrigger className="w-48" data-testid="pf-status"><SelectValue /></SelectTrigger>
                    <SelectContent>{["active", "draft", "out_of_stock"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
                </div>
                <div className="flex items-center justify-between">
                  <Label>Descriptions</Label>
                  <Button size="sm" variant="outline" onClick={aiGenerate} disabled={aiLoading} className="border-rose-200 text-rose-600 hover:bg-rose-50" data-testid="ai-generate">
                    <Sparkles className="h-4 w-4 mr-1" />{aiLoading ? "Generating…" : "AI Generate"}
                  </Button>
                </div>
                <div><Label className="text-xs text-slate-500">Short description</Label><Input value={f.short_description} onChange={(e) => set("short_description", e.target.value)} placeholder="Punchy one-liner" data-testid="pf-short" /></div>
                <div><Label className="text-xs text-slate-500">Long description</Label><textarea value={f.long_description} onChange={(e) => set("long_description", e.target.value)} rows={5} className="w-full border rounded-lg p-3 text-sm" data-testid="pf-long" /></div>
              </div>
            )}
            {tab === "pricing" && (
              <div className="space-y-4 max-w-md">
                <div><Label>Base price (₹)</Label><Input type="number" value={f.price} onChange={(e) => set("price", e.target.value)} data-testid="pf-price" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Discount %</Label><Input type="number" value={f.discount} onChange={(e) => set("discount", e.target.value)} data-testid="pf-discount" /></div>
                  <div><Label>GST / Tax %</Label><Input type="number" value={f.tax_rate} onChange={(e) => set("tax_rate", e.target.value)} data-testid="pf-tax" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Stock</Label><Input type="number" value={f.stock} onChange={(e) => set("stock", e.target.value)} data-testid="pf-stock" /></div>
                  <div><Label>Low-stock alert</Label><Input type="number" value={f.low_stock_threshold} onChange={(e) => set("low_stock_threshold", e.target.value)} data-testid="pf-threshold" /></div>
                </div>
              </div>
            )}
            {tab === "media" && (
              <div className="space-y-4">
                <div className="flex items-center gap-3 flex-wrap">
                  <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm cursor-pointer hover:bg-slate-800" data-testid="pf-upload-btn">
                    <Upload className="h-4 w-4" />{uploadingMedia ? "Uploading…" : "Upload images"}
                    <input type="file" accept="image/*" multiple className="hidden" onChange={handleMediaUpload} data-testid="pf-file-input" />
                  </label>
                  <span className="text-sm text-slate-400">or paste image URLs below</span>
                </div>
                <div><Label>Image URLs (one per line)</Label>
                  <textarea value={(f.images || []).join("\n")} onChange={(e) => set("images", e.target.value.split(/[\n,]/).map((s) => s.trim()).filter(Boolean))} rows={4} className="w-full border rounded-lg p-3 text-sm font-mono" placeholder="https://…" data-testid="pf-images" />
                </div>
                {(f.images || []).length > 0 && <p className="text-xs text-slate-400 -mb-1">Drag to reorder — the first image is your product's primary photo.</p>}
                <div className="flex flex-wrap gap-3">{(f.images || []).map((u, i) => (
                  <div key={`${u}-${i}`} draggable
                    onDragStart={() => setDragIdx(i)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => { if (dragIdx !== null && dragIdx !== i) moveImage(dragIdx, i); setDragIdx(null); }}
                    onDragEnd={() => setDragIdx(null)}
                    className={`relative group cursor-move rounded-lg transition-all ${dragIdx === i ? "opacity-40 scale-95" : ""}`}
                    data-testid={`media-thumb-${i}`}>
                    <img src={u} alt="" className={`h-20 w-20 rounded-lg object-cover border-2 ${i === 0 ? "border-slate-900" : "border-transparent"}`} />
                    {i === 0 && <span className="absolute bottom-1 left-1 text-[10px] font-medium bg-slate-900 text-white px-1.5 py-0.5 rounded" data-testid="media-primary-badge">Primary</span>}
                    <button onClick={() => set("images", f.images.filter((_, j) => j !== i))} className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-rose-500 text-white grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`img-del-${i}`}><X className="h-3 w-3" /></button>
                  </div>
                ))}</div>
              </div>
            )}
            {tab === "variants" && (
              <div>
                <div className="flex items-start justify-between mb-4">
                  <div><h4 className="font-semibold">Sub-products / Variants <span className="text-slate-400 font-normal">(e.g. 100ml, 200ml, 500ml)</span></h4><p className="text-sm text-slate-500">Each variant is a purchasable option on the product page.</p></div>
                  <Button size="sm" className="bg-rose-500 hover:bg-rose-600" onClick={() => set("variants", [...(f.variants || []), { id: Date.now().toString(), name: "", price: f.price || 0, stock: 0, image: "" }])} data-testid="add-variant"><Plus className="h-4 w-4 mr-1" />Add variant</Button>
                </div>
                <div className="space-y-3">
                  {(f.variants || []).map((v, i) => {
                    const low = v.name && Number(v.stock) <= Number(f.low_stock_threshold || 0);
                    return (
                    <div key={i} className="bg-slate-50 rounded-xl p-3 flex gap-3 items-start" data-testid={`variant-row-${i}`}>
                      <GripVertical className="h-5 w-5 text-slate-300 mt-2" />
                      <div className="grid grid-cols-3 gap-2 flex-1">
                        <Input placeholder="Name (e.g. 200ml)" value={v.name} onChange={(e) => { const a = [...f.variants]; a[i] = { ...v, name: e.target.value }; set("variants", a); }} data-testid={`variant-name-${i}`} />
                        <Input type="number" placeholder="Price ₹" value={v.price} onChange={(e) => { const a = [...f.variants]; a[i] = { ...v, price: e.target.value }; set("variants", a); }} data-testid={`variant-price-${i}`} />
                        <div className="relative">
                          <Input type="number" placeholder="Stock" value={v.stock} onChange={(e) => { const a = [...f.variants]; a[i] = { ...v, stock: e.target.value }; set("variants", a); }} data-testid={`variant-stock-${i}`} className={low ? "border-amber-400" : ""} />
                          {low && <span className="absolute -top-2 right-1 text-[10px] bg-amber-100 text-amber-700 border border-amber-200 px-1.5 rounded" data-testid={`variant-lowstock-${i}`}>low</span>}
                        </div>
                        <div className="col-span-3 flex gap-2 items-center">
                          <Input className="flex-1" placeholder="Or paste image URL" value={v.image} onChange={(e) => { const a = [...f.variants]; a[i] = { ...v, image: e.target.value }; set("variants", a); }} data-testid={`variant-image-${i}`} />
                          <label className="inline-flex items-center gap-1 px-3 h-10 rounded-lg border cursor-pointer text-sm bg-white hover:bg-slate-100" data-testid={`variant-upload-${i}`}><Upload className="h-4 w-4" /><input type="file" accept="image/*" className="hidden" onChange={(e) => handleVariantUpload(i, e)} /></label>
                          {v.image && <img src={v.image} alt="" className="h-10 w-10 rounded object-cover border" />}
                        </div>
                      </div>
                      <button onClick={() => set("variants", f.variants.filter((_, j) => j !== i))} className="text-rose-500 mt-2" data-testid={`variant-del-${i}`}><Trash2 className="h-4 w-4" /></button>
                    </div>
                    );
                  })}
                  {(f.variants || []).length === 0 && <p className="text-slate-400 text-sm">No variants — the base price/stock will be used.</p>}
                </div>
              </div>
            )}
            {tab === "packs" && (
              <div>
                <div className="flex items-start justify-between mb-4">
                  <div><h4 className="font-semibold">Packs <span className="text-slate-400 font-normal">(e.g. Pack of 3)</span></h4><p className="text-sm text-slate-500">Bundle multiple units at a special price.</p></div>
                  <Button size="sm" className="bg-rose-500 hover:bg-rose-600" onClick={() => set("packs", [...(f.packs || []), { id: Date.now().toString(), name: "", qty: 2, price: 0 }])} data-testid="add-pack"><Plus className="h-4 w-4 mr-1" />Add pack</Button>
                </div>
                <div className="space-y-3">
                  {(f.packs || []).map((pk, i) => (
                    <div key={i} className="bg-slate-50 rounded-xl p-3 grid grid-cols-[1fr_100px_120px_40px] gap-2 items-center" data-testid={`pack-row-${i}`}>
                      <Input placeholder="Name (e.g. Pack of 3)" value={pk.name} onChange={(e) => { const a = [...f.packs]; a[i] = { ...pk, name: e.target.value }; set("packs", a); }} data-testid={`pack-name-${i}`} />
                      <Input type="number" placeholder="Units" value={pk.qty} onChange={(e) => { const a = [...f.packs]; a[i] = { ...pk, qty: e.target.value }; set("packs", a); }} data-testid={`pack-qty-${i}`} />
                      <Input type="number" placeholder="Price ₹" value={pk.price} onChange={(e) => { const a = [...f.packs]; a[i] = { ...pk, price: e.target.value }; set("packs", a); }} data-testid={`pack-price-${i}`} />
                      <button onClick={() => set("packs", f.packs.filter((_, j) => j !== i))} className="text-rose-500" data-testid={`pack-del-${i}`}><Trash2 className="h-4 w-4" /></button>
                    </div>
                  ))}
                  {(f.packs || []).length === 0 && <p className="text-slate-400 text-sm">No packs added.</p>}
                </div>
              </div>
            )}
            {tab === "related" && (
              <div>
                <h4 className="font-semibold mb-1">Related products</h4>
                <p className="text-sm text-slate-500 mb-4">Shown as "You may also like" on the product page.</p>
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {allProducts.filter((p) => p.id !== editing?.id).map((p) => {
                    const checked = (f.related_ids || []).includes(p.id);
                    return (
                      <label key={p.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 cursor-pointer" data-testid={`related-${p.id}`}>
                        <input type="checkbox" checked={checked} onChange={() => set("related_ids", checked ? f.related_ids.filter((x) => x !== p.id) : [...(f.related_ids || []), p.id])} />
                        {p.images?.[0] && <img src={p.images[0]} alt="" className="h-8 w-8 rounded object-cover" />}
                        <span className="text-sm">{p.name}</span>
                      </label>
                    );
                  })}
                  {allProducts.filter((p) => p.id !== editing?.id).length === 0 && <p className="text-slate-400 text-sm">Add more products first.</p>}
                </div>
              </div>
            )}
            {tab === "seo" && (
              <div className="space-y-4 max-w-lg">
                <div><Label>SEO title</Label><Input value={f.seo.title} onChange={(e) => set("seo", { ...f.seo, title: e.target.value })} data-testid="pf-seo-title" /></div>
                <div><Label>Meta description</Label><textarea value={f.seo.description} onChange={(e) => set("seo", { ...f.seo, description: e.target.value })} rows={3} className="w-full border rounded-lg p-3 text-sm" data-testid="pf-seo-desc" /></div>
                <div><Label>Keywords</Label><Input value={f.seo.keywords} onChange={(e) => set("seo", { ...f.seo, keywords: e.target.value })} placeholder="comma, separated" data-testid="pf-seo-keywords" /></div>
              </div>
            )}
            {tab === "manufacturer" && (
              <div className="space-y-4 max-w-lg">
                <div><Label>Manufacturer name</Label><Input value={f.manufacturer.name} onChange={(e) => set("manufacturer", { ...f.manufacturer, name: e.target.value })} data-testid="pf-mfr-name" /></div>
                <div><Label>Brand</Label><Input value={f.manufacturer.brand} onChange={(e) => set("manufacturer", { ...f.manufacturer, brand: e.target.value })} data-testid="pf-mfr-brand" /></div>
                <div><Label>Country of origin</Label><Input value={f.manufacturer.country} onChange={(e) => set("manufacturer", { ...f.manufacturer, country: e.target.value })} data-testid="pf-mfr-country" /></div>
              </div>
            )}
          </div>
        </div>
        <DialogFooter className="px-6 py-4 border-t bg-slate-50 flex items-center sm:justify-between">
          <span className="text-xs text-slate-400 mr-auto hidden sm:block">Tip: use AI Generate on the Basic tab for short & long descriptions.</span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="pf-cancel">Cancel</Button>
            <Button onClick={save} className="bg-rose-500 hover:bg-rose-600" data-testid="pf-save">Save product</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Categories() {
  const [cats, setCats] = useState([]);
  const [name, setName] = useState("");
  const [subName, setSubName] = useState({});
  const load = () => api.get("/vendor/categories").then((r) => setCats(r.data));
  useEffect(() => { load(); }, []);
  const parents = cats.filter((c) => !c.parent_id);
  const addParent = async () => { if (!name.trim()) return; try { await api.post("/vendor/categories", { name: name.trim() }); setName(""); toast.success("Category added"); load(); } catch (e) { toast.error(apiError(e)); } };
  const addSub = async (pid) => { const n = (subName[pid] || "").trim(); if (!n) return; try { await api.post("/vendor/categories", { name: n, parent_id: pid }); setSubName({ ...subName, [pid]: "" }); toast.success("Sub-category added"); load(); } catch (e) { toast.error(apiError(e)); } };
  const del = async (id) => { await api.delete(`/vendor/categories/${id}`); load(); };
  return (
    <>
      <Head title="Categories" sub="Organise your storefront with categories & sub-categories" />
      <div className="flex gap-3 mb-8 max-w-lg">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="New category (e.g. Skincare)" data-testid="cat-name" />
        <Button onClick={addParent} className="bg-slate-900 hover:bg-slate-800" data-testid="cat-add"><Plus className="h-4 w-4 mr-1" />Add</Button>
      </div>
      <div className="space-y-4 max-w-2xl">
        {parents.map((p) => {
          const subs = cats.filter((c) => c.parent_id === p.id);
          return (
            <Card key={p.id} className="p-5" data-testid={`cat-${p.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 font-semibold"><FolderTree className="h-4 w-4 text-slate-400" />{p.name}</div>
                <button onClick={() => del(p.id)} className="text-slate-400 hover:text-rose-600" data-testid={`cat-del-${p.id}`}><Trash2 className="h-4 w-4" /></button>
              </div>
              <div className="mt-3 pl-6 space-y-2">
                {subs.map((s) => (
                  <div key={s.id} className="flex items-center justify-between text-sm text-slate-600" data-testid={`subcat-${s.id}`}>
                    <span className="flex items-center gap-1.5"><ChevronRight className="h-3.5 w-3.5 text-slate-300" />{s.name}</span>
                    <button onClick={() => del(s.id)} className="text-slate-400 hover:text-rose-600"><X className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
                <div className="flex gap-2 pt-1">
                  <Input value={subName[p.id] || ""} onChange={(e) => setSubName({ ...subName, [p.id]: e.target.value })} placeholder="Add sub-category" className="h-8 text-sm" data-testid={`subcat-input-${p.id}`} />
                  <Button size="sm" variant="outline" className="h-8" onClick={() => addSub(p.id)} data-testid={`subcat-add-${p.id}`}><Plus className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
            </Card>
          );
        })}
        {parents.length === 0 && <p className="text-slate-400">No categories yet. Create your first one above.</p>}
      </div>
    </>
  );
}

function Orders() {
  const { vendor } = useAuth();
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ total: 0, total_revenue: 0, counts: {}, last_synced: null });
  const [detail, setDetail] = useState(null);
  const [sel, setSel] = useState([]);
  const [bulkStatus, setBulkStatus] = useState("");
  const [partners, setPartners] = useState([]);
  const [filters, setFilters] = useState({ status: "all", q: "", payment_method: "all", payment_status: "all", carrier: "all", date_from: "", date_to: "" });

  const loadStats = () => api.get("/vendor/orders/stats").then((r) => setStats(r.data));
  const load = () => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all") params[k] = v; });
    return api.get("/vendor/orders", { params }).then((r) => { setRows(r.data); setSel([]); });
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [filters]);
  useEffect(() => { loadStats(); api.get("/vendor/delivery-partners").then((r) => setPartners(r.data)); }, []);

  const setF = (k, v) => setFilters((p) => ({ ...p, [k]: v }));
  const activeFilters = ["q", "payment_method", "payment_status", "carrier", "date_from", "date_to"].filter((k) => filters[k] && filters[k] !== "all").length;
  const clearFilters = () => setFilters((p) => ({ ...p, q: "", payment_method: "all", payment_status: "all", carrier: "all", date_from: "", date_to: "" }));
  const refresh = () => { Promise.all([load(), loadStats()]).then(() => toast.success("Order list synced")); };
  const setStat = async (id, s) => { try { const { data } = await api.patch(`/vendor/orders/${id}/status`, { status: s }); toast.success(`Order → ${STATUS_LABEL[s]}`); load(); loadStats(); if (detail?.id === id) setDetail(data); } catch (e) { toast.error(apiError(e)); } };
  const applyBulk = async () => {
    if (!bulkStatus || sel.length === 0) return;
    await Promise.all(sel.map((id) => api.patch(`/vendor/orders/${id}/status`, { status: bulkStatus }).catch(() => {})));
    toast.success(`${sel.length} orders → ${STATUS_LABEL[bulkStatus]}`); setBulkStatus(""); load(); loadStats();
  };
  const toggleSel = (id) => setSel((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const allSel = rows.length > 0 && sel.length === rows.length;
  const revenueInView = rows.reduce((a, o) => a + (o.total || 0), 0);
  const exportCsv = () => {
    const header = "order_no,customer,phone,email,total,payment_method,payment_status,carrier,tracking,status,date\n";
    const body = rows.map((o) => [o.order_no, o.customer_name, o.address?.phone || "", o.customer_email, o.total, o.payment_method, o.payment_status, o.carrier_name || "", o.tracking_number || "", o.status, o.created_at].join(",")).join("\n");
    const blob = new Blob([header + body], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "orders.csv"; a.click();
  };

  return (
    <>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Order Management</h1>
          <p className="text-slate-500 mt-1 text-sm" data-testid="orders-summary">
            {rows.length} orders • Revenue in view: <span className="font-semibold text-slate-700">{inr(revenueInView)}</span>
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right hidden sm:block">
            {stats.last_synced && <p className="text-xs text-slate-400">Last synced: {fmtDate(stats.last_synced)}</p>}
          </div>
          <Button variant="outline" onClick={exportCsv} data-testid="export-orders"><Upload className="h-4 w-4 mr-1 rotate-180" />Export CSV</Button>
          <Button onClick={refresh} className="bg-emerald-600 hover:bg-emerald-700" data-testid="update-live-status"><RefreshCw className="h-4 w-4 mr-1" />Update Live Status</Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-5" data-testid="status-tabs">
        {TAB_ORDER.map((s) => {
          const active = filters.status === s;
          const count = s === "all" ? stats.total : (stats.counts[s] || 0);
          return (
            <button key={s} onClick={() => setF("status", s)} data-testid={`tab-${s}`}
              className={`px-3.5 py-2 rounded-full text-sm font-medium border transition-colors ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
              {s === "all" ? "All Orders" : STATUS_LABEL[s]} <span className={active ? "text-white/70" : "text-slate-400"}>({count})</span>
            </button>
          );
        })}
      </div>

      <Card className="p-4 mb-6">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          <div className="relative lg:col-span-1">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={filters.q} onChange={(e) => setF("q", e.target.value)} placeholder="Order ID, customer, phone, product, tracking…" className="pl-9" data-testid="order-search" />
          </div>
          <Input type="date" value={filters.date_from} onChange={(e) => setF("date_from", e.target.value)} data-testid="order-date-from" />
          <Input type="date" value={filters.date_to} onChange={(e) => setF("date_to", e.target.value)} data-testid="order-date-to" />
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
          <Select value={filters.payment_method} onValueChange={(v) => setF("payment_method", v)}><SelectTrigger data-testid="order-payment-method"><SelectValue placeholder="Payment" /></SelectTrigger>
            <SelectContent>{["all", "upi", "card", "cod"].map((s) => <SelectItem key={s} value={s}>{s === "all" ? "All payments" : s.toUpperCase()}</SelectItem>)}</SelectContent></Select>
          <Select value={filters.carrier} onValueChange={(v) => setF("carrier", v)}><SelectTrigger data-testid="order-carrier"><SelectValue placeholder="Carrier" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All carriers</SelectItem>{partners.map((p) => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}</SelectContent></Select>
          <Select value={filters.payment_status} onValueChange={(v) => setF("payment_status", v)}><SelectTrigger data-testid="order-payment-status"><SelectValue placeholder="Payment status" /></SelectTrigger>
            <SelectContent>{["all", "paid", "pending", "refunded"].map((s) => <SelectItem key={s} value={s}>{s === "all" ? "Any payment status" : s}</SelectItem>)}</SelectContent></Select>
          <Button variant="outline" onClick={clearFilters} disabled={activeFilters === 0} data-testid="order-clear"><X className="h-4 w-4 mr-1" />Clear{activeFilters ? ` (${activeFilters})` : ""}</Button>
        </div>
      </Card>

      {sel.length > 0 && (
        <div className="flex items-center gap-3 mb-3 p-3 rounded-xl bg-slate-900 text-white" data-testid="bulk-bar">
          <span className="text-sm font-medium">{sel.length} selected</span>
          <Select value={bulkStatus} onValueChange={setBulkStatus}><SelectTrigger className="w-56 bg-white text-slate-900 h-9" data-testid="bulk-status-select"><SelectValue placeholder="Set status to…" /></SelectTrigger>
            <SelectContent>{[...VFLOW, "failed", "cancelled", "returned"].map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}</SelectContent></Select>
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-9" onClick={applyBulk} disabled={!bulkStatus} data-testid="bulk-apply">Apply</Button>
          <Button size="sm" variant="outline" className="h-9 bg-white text-slate-900" onClick={() => printLabels(rows.filter((o) => sel.includes(o.id)), vendor?.branding?.store_name)} data-testid="bulk-print-labels"><Printer className="h-4 w-4 mr-1" />Print labels</Button>
          <button className="ml-auto text-white/70 hover:text-white" onClick={() => setSel([])}><X className="h-4 w-4" /></button>
        </div>
      )}

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-slate-400 text-xs uppercase tracking-wider border-b border-slate-200">
              <tr>
                <th className="px-4 py-3"><input type="checkbox" checked={allSel} onChange={() => setSel(allSel ? [] : rows.map((o) => o.id))} data-testid="select-all" /></th>
                {["Order", "Date", "Customer", "Products", "Total", "Payment", "Carrier / Tracking", "Status", "Track"].map((h) => <th key={h} className="text-left px-4 py-3 font-medium whitespace-nowrap">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((o) => (
                <tr key={o.id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setDetail(o)} data-testid={`order-row-${o.id}`}>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={sel.includes(o.id)} onChange={() => toggleSel(o.id)} data-testid={`select-${o.id}`} /></td>
                  <td className="px-4 py-3 font-mono font-semibold text-rose-600 whitespace-nowrap">{o.order_no}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs whitespace-nowrap">{fmtDate(o.created_at)}</td>
                  <td className="px-4 py-3"><div className="font-medium">{o.customer_name}</div><div className="text-xs text-slate-400">{o.address?.phone || o.customer_email}</div></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 min-w-[220px]">
                      {o.items?.[0]?.image ? <img src={o.items[0].image} alt="" className="h-9 w-9 rounded object-cover border border-slate-200 shrink-0" /> : <div className="h-9 w-9 rounded bg-slate-100 grid place-items-center shrink-0"><Package className="h-4 w-4 text-slate-300" /></div>}
                      <div className="min-w-0"><div className="truncate max-w-[200px]">{o.items?.[0]?.name || "—"}</div><div className="text-xs text-slate-400">Qty {o.items?.[0]?.qty}{o.items?.length > 1 ? ` · +${o.items.length - 1} more` : ""}</div></div>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-semibold whitespace-nowrap">{inr(o.total)}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{o.payment_method?.toUpperCase()}<div className={`text-xs ${o.payment_status === "paid" ? "text-emerald-600" : o.payment_status === "refunded" ? "text-slate-400" : "text-amber-600"}`}>{o.payment_status}</div></td>
                  <td className="px-4 py-3 whitespace-nowrap">{o.carrier_name ? <><div>{o.carrier_name}</div>{o.tracking_number && <div className="text-xs text-slate-400 font-mono">{o.tracking_number}</div>}</> : <span className="text-slate-300">—</span>}</td>
                  <td className="px-4 py-3"><Badge variant="outline" className={`${orderBadge[o.status]} whitespace-nowrap`}>{STATUS_LABEL[o.status]}</Badge></td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>{o.tracking_url ? <a href={o.tracking_url} target="_blank" rel="noreferrer" className="text-rose-600 hover:text-rose-700" data-testid={`track-${o.id}`}><ExternalLink className="h-4 w-4" /></a> : <span className="text-slate-300">—</span>}</td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={10} className="px-4 py-10 text-center text-slate-400">No orders match your filters</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-4xl p-0 gap-0 max-h-[92vh] overflow-hidden">
          {detail && <OrderDetail order={detail} partners={partners} onStatus={setStat} onChanged={(o) => { setDetail(o); load(); loadStats(); }} />}
        </DialogContent>
      </Dialog>
    </>
  );
}

function printInvoice(order, storeName) {
  const rows = (order.items || []).map((it) => `<tr><td style="padding:6px 8px;border-bottom:1px solid #eee">${it.name}${it.variant ? " (" + it.variant + ")" : ""}</td><td style="text-align:center;padding:6px 8px;border-bottom:1px solid #eee">${it.qty}</td><td style="text-align:right;padding:6px 8px;border-bottom:1px solid #eee">₹${(it.price * it.qty).toLocaleString("en-IN")}</td></tr>`).join("");
  const a = order.address || {};
  const html = `<html><head><title>${order.order_no}</title></head><body style="font-family:Arial,sans-serif;color:#111;max-width:720px;margin:24px auto;padding:0 16px">
  <div style="display:flex;justify-content:space-between;align-items:center;border-bottom:2px solid #111;padding-bottom:12px">
    <h1 style="margin:0;font-size:22px">${storeName || "Invoice"}</h1>
    <div style="text-align:right"><div style="font-family:monospace;font-weight:700">${order.order_no}</div><div style="color:#666;font-size:12px">${fmtDate(order.created_at)}</div></div>
  </div>
  <p style="margin:12px 0 4px"><b>Bill to:</b> ${order.customer_name} · ${a.phone || ""}</p>
  <p style="margin:0;color:#444">${[a.line1, a.city, a.state, a.zip].filter(Boolean).join(", ")}</p>
  <table style="width:100%;border-collapse:collapse;margin-top:16px;font-size:14px"><thead><tr style="background:#f6f6f6"><th style="text-align:left;padding:8px">Item</th><th style="padding:8px">Qty</th><th style="text-align:right;padding:8px">Amount</th></tr></thead><tbody>${rows}</tbody></table>
  <div style="margin-top:12px;margin-left:auto;width:260px;font-size:14px">
    <div style="display:flex;justify-content:space-between;padding:2px 0"><span>Subtotal</span><span>₹${(order.subtotal || 0).toLocaleString("en-IN")}</span></div>
    ${order.discount ? `<div style="display:flex;justify-content:space-between;padding:2px 0;color:#0a0"><span>Discount</span><span>-₹${order.discount.toLocaleString("en-IN")}</span></div>` : ""}
    <div style="display:flex;justify-content:space-between;padding:2px 0"><span>GST</span><span>₹${(order.tax || 0).toLocaleString("en-IN")}</span></div>
    <div style="display:flex;justify-content:space-between;padding:2px 0"><span>Shipping</span><span>₹${(order.shipping || 0).toLocaleString("en-IN")}</span></div>
    <div style="display:flex;justify-content:space-between;padding:8px 0;border-top:1px solid #111;font-weight:700;font-size:16px"><span>Total (${(order.payment_method || "").toUpperCase()})</span><span>₹${(order.total || 0).toLocaleString("en-IN")}</span></div>
  </div>
  <p style="color:#888;font-size:12px;margin-top:24px">Thank you for your order.</p>
  </body></html>`;
  const w = window.open("", "_blank");
  if (!w) { toast.error("Allow pop-ups to print"); return; }
  w.document.write(html); w.document.close(); w.focus(); setTimeout(() => w.print(), 300);
}

function packingSlipHtml(order, storeName) {
  const a = order.address || {};
  const items = (order.items || []).map((it) => `<tr><td style="padding:8px;border-bottom:1px solid #eee">${it.name}</td><td style="padding:8px;border-bottom:1px solid #eee;color:#666">${it.variant || "—"}</td><td style="text-align:center;padding:8px;border-bottom:1px solid #eee;font-weight:700">${it.qty}</td></tr>`).join("");
  return `<div style="page-break-after:always;font-family:Arial,sans-serif;color:#111;max-width:720px;margin:0 auto;padding:24px 16px">
    <div style="display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #111;padding-bottom:10px">
      <div><div style="font-size:11px;letter-spacing:2px;color:#888">PACKING SLIP</div><h1 style="margin:2px 0 0;font-size:20px">${storeName || "Store"}</h1></div>
      <div style="text-align:right"><div style="font-family:monospace;font-weight:700;font-size:16px">${order.order_no}</div><div style="color:#666;font-size:12px">${fmtDate(order.created_at)}</div></div>
    </div>
    <div style="display:flex;gap:24px;margin-top:14px;font-size:13px">
      <div style="flex:1"><div style="color:#888;font-size:11px">SHIP TO</div><div style="font-weight:700">${order.customer_name}</div><div>${a.line1 || ""}</div><div>${[a.city, a.state, a.zip].filter(Boolean).join(", ")}</div><div>${a.phone || ""}</div></div>
      <div style="flex:1"><div style="color:#888;font-size:11px">CARRIER</div><div style="font-weight:700">${order.carrier_name || "—"}</div><div style="font-family:monospace">${order.tracking_number || ""}</div></div>
    </div>
    <table style="width:100%;border-collapse:collapse;margin-top:16px;font-size:13px"><thead><tr style="background:#f6f6f6"><th style="text-align:left;padding:8px">Item</th><th style="text-align:left;padding:8px">Variant</th><th style="padding:8px">Qty</th></tr></thead><tbody>${items}</tbody></table>
    <p style="color:#888;font-size:12px;margin-top:16px">Total items: ${(order.items || []).reduce((s, i) => s + i.qty, 0)}. Please verify contents before dispatch.</p>
  </div>`;
}

function printDoc(inner, title) {
  const w = window.open("", "_blank");
  if (!w) { toast.error("Allow pop-ups to print"); return; }
  w.document.write(`<html><head><title>${title}</title></head><body style="margin:0">${inner}</body></html>`);
  w.document.close(); w.focus(); setTimeout(() => w.print(), 300);
}

function printPackingSlip(order, storeName) {
  printDoc(packingSlipHtml(order, storeName), `Packing slip ${order.order_no}`);
}

function labelHtml(order, storeName) {
  const a = order.address || {};
  return `<div style="page-break-after:always;font-family:Arial,sans-serif;color:#111;width:384px;margin:16px auto;border:2px solid #111;border-radius:8px;padding:16px">
    <div style="font-size:11px;color:#888">FROM</div><div style="font-weight:700;margin-bottom:8px">${storeName || "Store"}</div>
    <div style="font-size:11px;color:#888">SHIP TO</div>
    <div style="font-weight:700;font-size:16px">${order.customer_name}</div>
    <div style="font-size:14px">${a.line1 || ""}</div>
    <div style="font-size:14px">${[a.city, a.state].filter(Boolean).join(", ")} — ${a.zip || ""}</div>
    <div style="font-size:14px">${a.phone || ""}</div>
    <div style="display:flex;justify-content:space-between;margin-top:12px;padding-top:10px;border-top:1px dashed #999;font-size:13px">
      <div><div style="color:#888;font-size:10px">ORDER</div><div style="font-family:monospace;font-weight:700">${order.order_no}</div></div>
      <div style="text-align:right"><div style="color:#888;font-size:10px">${(order.carrier_name || "CARRIER").toUpperCase()}</div><div style="font-family:monospace;font-weight:700">${order.tracking_number || "—"}</div></div>
    </div>
    <div style="margin-top:8px;font-size:12px;color:#666">${(order.items || []).reduce((s, i) => s + i.qty, 0)} item(s) · ${(order.payment_method || "").toUpperCase()} ${order.payment_status || ""}</div>
  </div>`;
}

function printLabels(orders, storeName) {
  if (!orders.length) return;
  printDoc(orders.map((o) => labelHtml(o, storeName)).join(""), `Shipping labels (${orders.length})`);
}

function OrderDetail({ order, partners, onStatus, onChanged }) {
  const { vendor } = useAuth();
  const [tab, setTab] = useState("overview");
  const [carrier, setCarrier] = useState(order.carrier_code || "");
  const [tracking, setTracking] = useState(order.tracking_number || "");
  const [comment, setComment] = useState("");
  const flowIdx = VFLOW.indexOf(order.status);
  const terminal = ["failed", "cancelled", "returned"].includes(order.status);
  const nextStatus = flowIdx >= 0 && flowIdx < VFLOW.length - 1 ? VFLOW[flowIdx + 1] : null;
  const activePartners = partners.filter((p) => p.active);
  const taxable = Math.max(0, (order.subtotal || 0) - (order.discount || 0));

  const saveShipping = async () => {
    try { const { data } = await api.patch(`/vendor/orders/${order.id}/shipping`, { carrier_code: carrier || null, tracking_number: tracking }); toast.success("Shipping updated"); onChanged(data); }
    catch (e) { toast.error(apiError(e)); }
  };
  const addComment = async () => {
    if (!comment.trim()) return;
    try { const { data } = await api.post(`/vendor/orders/${order.id}/comments`, { text: comment }); setComment(""); onChanged(data); }
    catch (e) { toast.error(apiError(e)); }
  };
  const tabs = [["overview", "Overview"], ["items", `Items (${order.items?.length || 0})`], ["timeline", `Timeline (${order.timeline?.length || 0})`], ["comments", `Comments (${order.comments?.length || 0})`]];
  const Info = ({ icon: Icon, title, children, testid }) => (
    <Card className="p-4" data-testid={testid}><div className="flex items-center gap-2 text-slate-500 text-sm mb-2"><Icon className="h-4 w-4" />{title}</div>{children}</Card>
  );

  return (
    <div className="flex flex-col max-h-[92vh]">
      <DialogHeader className="px-6 pt-6 pb-4 border-b">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <DialogTitle className="text-2xl font-display">Order <span className="font-mono">{order.order_no}</span></DialogTitle>
            <Badge variant="outline" className={orderBadge[order.status]}>{STATUS_LABEL[order.status]}</Badge>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => printPackingSlip(order, vendor?.branding?.store_name)} data-testid="order-packing-slip"><FileText className="h-4 w-4 mr-1" />Packing slip</Button>
            <Button variant="outline" size="sm" onClick={() => printInvoice(order, vendor?.branding?.store_name)} data-testid="order-print"><Printer className="h-4 w-4 mr-1" />Print</Button>
            <Button size="sm" className="bg-rose-600 hover:bg-rose-700" onClick={() => printInvoice(order, vendor?.branding?.store_name)} data-testid="order-pdf"><FileText className="h-4 w-4 mr-1" />PDF</Button>
          </div>
        </div>
        <p className="text-sm text-slate-500 text-left">Placed on {fmtDate(order.created_at)} • {order.items?.length || 0} items • {inr(order.total)}</p>
        <div className="flex gap-5 mt-3">
          {tabs.map(([k, l]) => <button key={k} onClick={() => setTab(k)} data-testid={`order-tab-${k}`} className={`text-sm pb-2 border-b-2 -mb-[17px] transition-colors ${tab === k ? "border-rose-600 text-rose-600 font-medium" : "border-transparent text-slate-500 hover:text-slate-800"}`}>{l}</button>)}
        </div>
      </DialogHeader>

      <div className="overflow-y-auto px-6 py-5 space-y-5">
        {tab === "overview" && <>
          {terminal ? (
            <Card className={`p-4 ${orderBadge[order.status]}`} data-testid="order-terminal-banner"><div className="font-medium">This order is {STATUS_LABEL[order.status].toLowerCase()}.</div></Card>
          ) : (
            <Card className="p-5" data-testid="order-stepper">
              <div className="flex items-center justify-between">
                {VFLOW.map((s, i) => {
                  const done = flowIdx >= i;
                  const tl = (order.timeline || []).find((t) => t.status === s);
                  return (
                    <div key={s} className="flex-1 flex flex-col items-center relative">
                      {i > 0 && <div className={`absolute top-4 right-1/2 w-full h-0.5 ${flowIdx >= i ? "bg-emerald-500" : "bg-slate-200"}`} />}
                      <div className={`relative z-10 h-8 w-8 rounded-full grid place-items-center text-xs font-semibold ${done ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-400 border border-slate-200"}`}>{done ? <CheckCircle2 className="h-4 w-4" /> : i + 1}</div>
                      <div className={`text-xs mt-2 text-center ${done ? "text-slate-800 font-medium" : "text-slate-400"}`}>{STATUS_LABEL[s]}</div>
                      <div className="text-[10px] text-slate-400 mt-0.5">{tl ? fmtDate(tl.at).split(",")[0] : "—"}</div>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          <div className="grid md:grid-cols-3 gap-4">
            <div className="md:col-span-2 space-y-4">
              <Info icon={Package} title="Ordered products" testid="detail-products">
                <div className="divide-y">
                  {order.items?.map((it, i) => (
                    <div key={i} className="py-3 flex items-center gap-3">
                      {it.image ? <img src={it.image} alt="" className="h-12 w-12 rounded object-cover border" /> : <div className="h-12 w-12 rounded bg-slate-100 grid place-items-center"><Package className="h-5 w-5 text-slate-300" /></div>}
                      <div className="flex-1 min-w-0"><div className="font-medium truncate">{it.name}</div>{it.variant && <div className="text-xs text-slate-400">{it.variant}</div>}<div className="text-xs text-slate-400">Qty {it.qty}</div></div>
                      <div className="text-right"><div className="font-medium">{inr(it.price * it.qty)}</div><div className="text-xs text-slate-400">{inr(it.price)} × {it.qty}</div></div>
                    </div>
                  ))}
                </div>
              </Info>
              <Info icon={IndianRupee} title="Bill summary" testid="detail-bill">
                <div className="text-sm space-y-1">
                  <Row l="Selling price" v={order.subtotal} />
                  {order.discount > 0 && <div className="flex justify-between text-emerald-600"><span>Discount</span><span>-{inr(order.discount)}</span></div>}
                  <Row l="Taxable value" v={taxable} />
                  <Row l="Total GST" v={order.tax} />
                  <Row l="Shipping" v={order.shipping} />
                  <div className="flex justify-between font-semibold text-base pt-2 border-t"><span>Total payable</span><span className="text-rose-600">{inr(order.total)}</span></div>
                  <div className="text-xs text-slate-400">Paid via {order.payment_method?.toUpperCase()} · {order.payment_status}</div>
                  {order.loyalty_points > 0 && <div className="mt-2 text-xs bg-amber-50 text-amber-700 rounded-lg px-3 py-2 flex items-center gap-2"><Star className="h-3.5 w-3.5" />Customer earned {order.loyalty_points} loyalty points</div>}
                </div>
              </Info>
              <Info icon={Truck} title="Shipping & tracking" testid="detail-shipping">
                <div className="grid sm:grid-cols-2 gap-3">
                  <div><Label className="text-xs">Carrier</Label>
                    <Select value={carrier || "none"} onValueChange={(v) => setCarrier(v === "none" ? "" : v)}><SelectTrigger data-testid="assign-carrier"><SelectValue placeholder="Select carrier" /></SelectTrigger>
                      <SelectContent><SelectItem value="none">No carrier</SelectItem>{activePartners.map((p) => <SelectItem key={p.code} value={p.code}>{p.name}{p.own_fleet ? " (Own fleet)" : ""}</SelectItem>)}</SelectContent></Select>
                  </div>
                  <div><Label className="text-xs">Tracking number</Label><Input value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="AWB / tracking no." data-testid="assign-tracking" /></div>
                </div>
                <div className="flex items-center gap-3 mt-3">
                  <Button size="sm" onClick={saveShipping} className="bg-slate-900 hover:bg-slate-800" data-testid="save-shipping">Save tracking</Button>
                  {order.tracking_url && <a href={order.tracking_url} target="_blank" rel="noreferrer" className="text-sm text-rose-600 inline-flex items-center gap-1" data-testid="detail-track-link">Track shipment <ExternalLink className="h-3.5 w-3.5" /></a>}
                </div>
              </Info>
            </div>
            <div className="space-y-4">
              <Info icon={Users} title="Customer" testid="detail-customer">
                <div className="font-medium">{order.customer_name}</div>
                {order.address?.phone && <div className="text-sm text-slate-500 flex items-center gap-1.5 mt-1"><Phone className="h-3.5 w-3.5" />{order.address.phone}</div>}
                {order.customer_email && <div className="text-sm text-slate-500 flex items-center gap-1.5"><MessageSquare className="h-3.5 w-3.5" />{order.customer_email}</div>}
              </Info>
              <Info icon={MapPin} title="Shipping address" testid="detail-shipping-address">
                <div className="text-sm text-slate-600">{order.address?.line1}</div>
                <div className="text-sm text-slate-500">{[order.address?.city, order.address?.state].filter(Boolean).join(", ")} — {order.address?.zip}</div>
              </Info>
              <Info icon={FileText} title="Billing address" testid="detail-billing-address">
                <Badge variant="outline" className="mb-2 bg-slate-50 text-slate-500 text-[10px]">SAME AS SHIPPING</Badge>
                <div className="text-sm text-slate-600">{order.address?.line1}</div>
                <div className="text-sm text-slate-500">{[order.address?.city, order.address?.state].filter(Boolean).join(", ")} — {order.address?.zip}</div>
              </Info>
            </div>
          </div>

          <Card className="p-4">
            <div className="text-slate-500 text-sm mb-3">Update status</div>
            <div className="flex flex-wrap gap-2">
              {nextStatus && !terminal && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-8 text-xs" onClick={() => onStatus(order.id, nextStatus)} data-testid={`advance-${nextStatus}`}>Mark {STATUS_LABEL[nextStatus]}</Button>}
              {VFLOW.map((s) => <Button key={s} size="sm" variant={order.status === s ? "default" : "outline"} className="h-8 text-xs" onClick={() => onStatus(order.id, s)} data-testid={`set-status-${s}`}>{STATUS_LABEL[s]}</Button>)}
              {["failed", "cancelled", "returned"].map((s) => <Button key={s} size="sm" variant="outline" className={`h-8 text-xs ${order.status === s ? "" : "border-rose-200 text-rose-600"}`} onClick={() => onStatus(order.id, s)} data-testid={`set-status-${s}`}>{STATUS_LABEL[s]}</Button>)}
            </div>
          </Card>
        </>}

        {tab === "items" && (
          <div className="border rounded-lg divide-y">
            {order.items?.map((it, i) => (
              <div key={i} className="p-4 flex items-center gap-3">
                {it.image ? <img src={it.image} alt="" className="h-12 w-12 rounded object-cover border" /> : <div className="h-12 w-12 rounded bg-slate-100" />}
                <div className="flex-1"><div className="font-medium">{it.name}</div>{it.variant && <div className="text-xs text-slate-400">{it.variant}</div>}</div>
                <div className="text-sm text-slate-500">Qty {it.qty}</div>
                <div className="font-medium w-24 text-right">{inr(it.price * it.qty)}</div>
              </div>
            ))}
          </div>
        )}

        {tab === "timeline" && (
          <div className="space-y-3" data-testid="detail-timeline">
            {(order.timeline || []).map((t, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="h-8 w-8 rounded-full bg-slate-100 grid place-items-center shrink-0"><Clock className="h-4 w-4 text-slate-400" /></div>
                <div><div className="font-medium text-sm">{STATUS_LABEL[t.status] || t.status}</div><div className="text-xs text-slate-400">{fmtDate(t.at)}</div></div>
              </div>
            ))}
          </div>
        )}

        {tab === "comments" && (
          <div className="space-y-4" data-testid="detail-comments">
            <div className="flex gap-2">
              <Input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Add an internal note…" data-testid="comment-input" onKeyDown={(e) => e.key === "Enter" && addComment()} />
              <Button onClick={addComment} className="bg-slate-900 hover:bg-slate-800" data-testid="comment-add">Add</Button>
            </div>
            {(order.comments || []).length === 0 && <div className="text-slate-400 text-sm">No comments yet.</div>}
            {(order.comments || []).map((c) => (
              <div key={c.id} className="bg-slate-50 rounded-lg p-3"><div className="text-sm">{c.text}</div><div className="text-xs text-slate-400 mt-1">{c.author} · {fmtDate(c.at)}</div></div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
const Row = ({ l, v }) => <div className="flex justify-between text-slate-600"><span>{l}</span><span>{inr(v)}</span></div>;

function Customers() {
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState(null);
  const [notes, setNotes] = useState("");
  const load = () => api.get("/vendor/customers").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const save = async () => { await api.patch(`/vendor/customers/${sel.id}`, { notes }); toast.success("Saved"); setSel(null); load(); };
  const toggleBlock = async (c) => { await api.patch(`/vendor/customers/${c.id}`, { blocked: !c.blocked }); toast.success(c.blocked ? "Unblocked" : "Blocked"); load(); };
  return (
    <>
      <Head title="Customers" sub="Your store's shoppers" />
      <Card>
        <table className="w-full text-sm">
          <thead className="text-slate-400 text-xs uppercase tracking-wider border-b border-slate-200"><tr>{["Customer", "Orders", "Spent", "Status", ""].map((h) => <th key={h} className="text-left px-4 py-3 font-medium">{h}</th>)}</tr></thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`customer-row-${c.id}`}>
                <td className="px-4 py-3"><div className="font-medium">{c.name}</div><div className="text-slate-500 text-xs">{c.email}</div></td>
                <td className="px-4 py-3">{c.order_count}</td>
                <td className="px-4 py-3">{inr(c.total_spent)}</td>
                <td className="px-4 py-3">{c.blocked ? <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200">Blocked</Badge> : <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Active</Badge>}</td>
                <td className="px-4 py-3"><div className="flex gap-2 justify-end">
                  <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => { setSel(c); setNotes(c.notes || ""); }} data-testid={`note-${c.id}`}>Notes</Button>
                  <Button size="sm" variant="outline" className={`h-8 text-xs ${c.blocked ? "" : "border-rose-200 text-rose-600"}`} onClick={() => toggleBlock(c)} data-testid={`block-${c.id}`}>{c.blocked ? "Unblock" : "Block"}</Button>
                </div></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">No customers yet</td></tr>}
          </tbody>
        </table>
      </Card>
      <Dialog open={!!sel} onOpenChange={(v) => !v && setSel(null)}>
        <DialogContent><DialogHeader><DialogTitle>Notes — {sel?.name}</DialogTitle></DialogHeader>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} className="w-full border rounded-lg p-3 text-sm" data-testid="customer-notes" />
          <DialogFooter><Button onClick={save} className="bg-slate-900 hover:bg-slate-800" data-testid="save-notes">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function VendorCoupons() {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ code: "", discount_percent: 10 });
  const load = () => api.get("/vendor/coupons").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const create = async () => { try { await api.post("/vendor/coupons", f); toast.success("Coupon created"); setF({ code: "", discount_percent: 10 }); load(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <>
      <Head title="Store Coupons" sub="Discounts customers apply at checkout" />
      <div className="flex gap-3 mb-6 max-w-lg">
        <Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} placeholder="SAVE20" className="font-mono" data-testid="vc-code" />
        <Input type="number" value={f.discount_percent} onChange={(e) => setF({ ...f, discount_percent: Number(e.target.value) })} className="w-24" data-testid="vc-discount" />
        <Button onClick={create} className="bg-slate-900 hover:bg-slate-800" data-testid="vc-create"><Plus className="h-4 w-4" /></Button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.map((c) => (
          <Card key={c.id} className="p-5 flex items-center justify-between" data-testid={`vc-${c.code}`}>
            <div><div className="font-mono font-semibold text-lg">{c.code}</div><div className="text-slate-500 text-sm">{c.discount_percent}% off</div></div>
            <button onClick={async () => { await api.delete(`/vendor/coupons/${c.id}`); load(); }} className="text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
          </Card>
        ))}
        {rows.length === 0 && <div className="text-slate-400">No coupons yet.</div>}
      </div>
    </>
  );
}

function DeliveryPartners() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const load = () => api.get("/vendor/delivery-partners").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const blank = { name: "", code: "", tracking_url: "", api_key: "", support_phone: "", own_fleet: false, active: true };
  const openNew = () => { setForm({ ...blank }); setOpen(true); };
  const openEdit = (p) => { setForm({ ...p }); setOpen(true); };
  const setFf = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    try {
      if (form.id) await api.put(`/vendor/delivery-partners/${form.id}`, form);
      else await api.post("/vendor/delivery-partners", form);
      toast.success("Partner saved"); setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => { try { await api.delete(`/vendor/delivery-partners/${id}`); toast.success("Partner removed"); load(); } catch (e) { toast.error(apiError(e)); } };

  return (
    <>
      <Head title="Delivery Partners" sub="Configure the courier / logistics partners available for dispatch.">
        <Button onClick={openNew} className="bg-rose-600 hover:bg-rose-700" data-testid="add-partner"><Plus className="h-4 w-4 mr-1" />Add partner</Button>
      </Head>
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-slate-400 text-xs uppercase tracking-wider border-b border-slate-200"><tr>{["Partner", "Code", "Tracking URL", "Support", "API key", "Status", ""].map((h) => <th key={h} className="text-left px-4 py-3 font-medium">{h}</th>)}</tr></thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`partner-row-${p.code}`}>
                  <td className="px-4 py-3"><div className="flex items-center gap-2"><Truck className="h-4 w-4 text-rose-500" /><span className="font-medium">{p.name}</span>{p.own_fleet && <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">Own Fleet</Badge>}</div></td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-500">{p.code}</td>
                  <td className="px-4 py-3 max-w-[280px] truncate">{p.tracking_url ? <a href={p.tracking_url} target="_blank" rel="noreferrer" className="text-rose-600 hover:underline inline-flex items-center gap-1 truncate">{p.tracking_url}<ExternalLink className="h-3 w-3 shrink-0" /></a> : <span className="text-slate-300">—</span>}</td>
                  <td className="px-4 py-3 text-slate-500">{p.support_phone || "—"}</td>
                  <td className="px-4 py-3 text-slate-400 text-xs">{p.api_key ? "•••• set" : "Not set"}</td>
                  <td className="px-4 py-3">{p.active ? <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Active</Badge> : <Badge variant="outline" className="bg-slate-100 text-slate-500 border-slate-200">Disabled</Badge>}</td>
                  <td className="px-4 py-3"><div className="flex gap-3 justify-end">
                    <button onClick={() => openEdit(p)} className="text-slate-400 hover:text-slate-700" data-testid={`edit-partner-${p.code}`}><Edit className="h-4 w-4" /></button>
                    <button onClick={() => remove(p.id)} className="text-slate-400 hover:text-rose-600" data-testid={`delete-partner-${p.code}`}><Trash2 className="h-4 w-4" /></button>
                  </div></td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">No delivery partners yet. Add one to start dispatching orders.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
      <div className="mt-4 rounded-xl bg-blue-50 border border-blue-100 p-4 text-sm text-blue-800" data-testid="tracking-hint">
        💡 <span className="font-medium">Tracking URL template</span> — use <code className="bg-white px-1.5 py-0.5 rounded font-mono text-xs">{"{tracking}"}</code> as a placeholder; it is replaced with the tracking number at runtime. Example: <code className="bg-white px-1.5 py-0.5 rounded font-mono text-xs">https://www.delhivery.com/track/package/{"{tracking}"}</code>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display text-xl">{form?.id ? "Edit" : "Add"} Delivery Partner</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-3">
              <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setFf("name", e.target.value)} placeholder="e.g. Delhivery" data-testid="partner-name" /></div>
              <div><Label>Code *</Label><Input value={form.code} onChange={(e) => setFf("code", e.target.value.toUpperCase())} placeholder="DELHIVERY (unique, uppercase)" className="font-mono" disabled={!!form.id} data-testid="partner-code" /></div>
              <div><Label>Tracking URL template</Label><Input value={form.tracking_url} onChange={(e) => setFf("tracking_url", e.target.value)} placeholder="https://example.com/track?awb={tracking}" data-testid="partner-tracking-url" /><p className="text-xs text-slate-400 mt-1">Use {"{tracking}"} as placeholder</p></div>
              <div><Label>API Key (optional)</Label><Input value={form.api_key} onChange={(e) => setFf("api_key", e.target.value)} placeholder="For automated pickup integration" data-testid="partner-api-key" /></div>
              <div><Label>Support phone</Label><Input value={form.support_phone} onChange={(e) => setFf("support_phone", e.target.value)} placeholder="1800-XXX-XXXX" data-testid="partner-support" /></div>
              <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={form.own_fleet} onChange={(e) => setFf("own_fleet", e.target.checked)} data-testid="partner-own-fleet" />🛵 Own delivery fleet (skip external AWB)</label>
              <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={form.active} onChange={(e) => setFf("active", e.target.checked)} data-testid="partner-active" />Active — available for order dispatch</label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="partner-cancel">Cancel</Button>
            <Button onClick={save} className="bg-rose-600 hover:bg-rose-700" data-testid="partner-save">Save partner</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function StoreSettings() {
  const { reload } = useAuth();
  const [v, setV] = useState(null);
  const themeOverride = useRef(false);
  useEffect(() => {
    api.get("/vendor/settings").then((r) => {
      setV(r.data);
      themeOverride.current = (r.data.theme_id || "classic") !== themeForCategory(r.data.business_category || "");
    });
  }, []);
  if (!v) return <div className="text-slate-500">Loading…</div>;
  const b = v.branding || {};
  const pc = v.payment_config || { methods: { upi: true, card: true, cod: true }, upi_id: "", gateway: { provider: "razorpay", key_id: "", key_secret: "" } };
  const save = async () => {
    try { await api.put("/vendor/settings", { branding: v.branding, shipping_zones: v.shipping_zones, payment_config: pc, business_category: v.business_category, theme_id: v.theme_id }); toast.success("Settings saved"); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const onCategory = (cid) => setV((prev) => ({ ...prev, business_category: cid, theme_id: themeOverride.current ? (prev.theme_id || "classic") : themeForCategory(cid) }));
  const onTheme = (tid) => { themeOverride.current = true; setV((prev) => ({ ...prev, theme_id: tid })); };
  const setBrand = (k, val) => setV({ ...v, branding: { ...v.branding, [k]: val } });
  const uploadLogo = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try { const [url] = await uploadFiles([file]); setV((prev) => ({ ...prev, branding: { ...prev.branding, logo: url } })); toast.success("Logo uploaded"); }
    catch (err) { toast.error(apiError(err)); } finally { e.target.value = ""; }
  };
  const setMethod = (m, on) => setV({ ...v, payment_config: { ...pc, methods: { ...pc.methods, [m]: on } } });
  const setGateway = (k, val) => setV({ ...v, payment_config: { ...pc, gateway: { ...pc.gateway, [k]: val } } });
  const setUpiId = (val) => setV({ ...v, payment_config: { ...pc, upi_id: val } });
  const METHODS = [["upi", "UPI", "Google Pay, PhonePe, etc."], ["card", "Credit / Debit Card", "Visa, Mastercard, RuPay"], ["cod", "Cash on Delivery", "Pay on delivery"]];
  return (
    <>
      <Head title="Store Settings" sub="Branding, shipping & payments" />
      <div className="grid lg:grid-cols-2 gap-6 max-w-4xl">
        <Card className="p-6 space-y-4">
          <h3 className="font-display font-semibold text-lg">Branding</h3>
          <div><Label>Store name</Label><Input value={b.store_name || ""} onChange={(e) => setBrand("store_name", e.target.value)} data-testid="set-store-name" /></div>
          <div><Label>Store slug</Label><Input value={v.slug} disabled className="font-mono bg-slate-50" /></div>
          <div><Label>Logo</Label>
            <div className="flex items-center gap-3">
              {b.logo ? <img src={b.logo} alt="logo" className="h-12 w-12 rounded-lg object-cover border" data-testid="logo-preview" /> : <div className="h-12 w-12 rounded-lg bg-slate-100 grid place-items-center text-slate-300"><Store className="h-5 w-5" /></div>}
              <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-900 text-white text-sm cursor-pointer hover:bg-slate-800" data-testid="logo-upload-btn"><Upload className="h-4 w-4" />Upload logo<input type="file" accept="image/*" className="hidden" onChange={uploadLogo} data-testid="logo-file-input" /></label>
              {b.logo && <button onClick={() => setBrand("logo", "")} className="text-slate-400 hover:text-rose-600" data-testid="logo-remove"><X className="h-4 w-4" /></button>}
            </div>
            <Input value={b.logo || ""} onChange={(e) => setBrand("logo", e.target.value)} placeholder="…or paste a logo URL" className="mt-2 font-mono text-xs" data-testid="set-logo" />
          </div>
          <div><Label>Banner URL</Label><Input value={b.banner || ""} onChange={(e) => setBrand("banner", e.target.value)} data-testid="set-banner" /></div>
          <div><Label>WhatsApp number / link</Label><Input value={b.whatsapp || ""} onChange={(e) => setBrand("whatsapp", e.target.value)} placeholder="+91 98765 43210 or https://wa.me/…" data-testid="set-whatsapp" /></div>
          <div><Label>Theme color</Label><div className="flex gap-2 items-center"><input type="color" value={b.theme_color || "#E11D48"} onChange={(e) => setBrand("theme_color", e.target.value)} className="h-10 w-16 rounded border" data-testid="set-color" /><Input value={b.theme_color || ""} onChange={(e) => setBrand("theme_color", e.target.value)} className="font-mono" /></div></div>
        </Card>
        <Card className="p-6 space-y-4">
          <h3 className="font-display font-semibold text-lg">Shipping zones (₹)</h3>
          {(v.shipping_zones || []).map((z, i) => (
            <div key={i} className="flex gap-2 items-center">
              <Input value={z.name} onChange={(e) => { const zs = [...v.shipping_zones]; zs[i] = { ...z, name: e.target.value }; setV({ ...v, shipping_zones: zs }); }} data-testid={`zone-name-${i}`} />
              <Input type="number" value={z.rate} onChange={(e) => { const zs = [...v.shipping_zones]; zs[i] = { ...z, rate: Number(e.target.value) }; setV({ ...v, shipping_zones: zs }); }} className="w-28" data-testid={`zone-rate-${i}`} />
              <button onClick={() => setV({ ...v, shipping_zones: v.shipping_zones.filter((_, j) => j !== i) })} className="text-slate-400 hover:text-rose-600"><X className="h-4 w-4" /></button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => setV({ ...v, shipping_zones: [...(v.shipping_zones || []), { id: Date.now().toString(), name: "New zone", rate: 40 }] })} data-testid="add-zone"><Plus className="h-4 w-4 mr-1" />Add zone</Button>
        </Card>
        <Card className="p-6 space-y-4 lg:col-span-2" data-testid="storefront-theme">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2"><LayoutGrid className="h-5 w-5 text-slate-400" /><h3 className="font-display font-semibold text-lg">Storefront theme</h3></div>
            <a href={`/${v.slug}`} target="_blank" rel="noreferrer" className="text-sm text-rose-600 inline-flex items-center gap-1 hover:underline" data-testid="preview-theme">View storefront <ExternalLink className="h-3.5 w-3.5" /></a>
          </div>
          <p className="text-sm text-slate-500 -mt-2">Pick your business category and we suggest a matching layout. Override it anytime — your brand colours and logo always stay on top of the layout.</p>
          <div>
            <Label>Business category</Label>
            <Select value={v.business_category || "none"} onValueChange={(val) => onCategory(val === "none" ? "" : val)}>
              <SelectTrigger className="mt-1" data-testid="set-category"><SelectValue placeholder="Select category" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Not set</SelectItem>
                {BUSINESS_CATEGORIES.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {THEMES.map((t) => {
              const active = (v.theme_id || "classic") === t.id;
              const suggested = v.business_category && themeForCategory(v.business_category) === t.id;
              return (
                <button key={t.id} onClick={() => onTheme(t.id)} data-testid={`theme-${t.id}`}
                  className={`text-left p-4 rounded-xl border transition-colors relative ${active ? "border-transparent bg-slate-900 text-white" : "border-slate-200 bg-white hover:bg-slate-50"}`}>
                  {suggested && <Badge className={`absolute top-2 right-2 text-[10px] border-0 ${active ? "bg-white text-slate-900" : "bg-amber-100 text-amber-800"}`}>Suggested</Badge>}
                  <div className="font-medium">{t.label}</div>
                  <div className={`text-[11px] uppercase tracking-wider mt-0.5 ${active ? "text-white/60" : "text-slate-400"}`}>{t.tagline}</div>
                  <div className={`text-xs mt-2 ${active ? "text-white/80" : "text-slate-500"}`}>{t.desc}</div>
                  {active && <div className="mt-2 text-xs font-medium text-emerald-300">Active</div>}
                </button>
              );
            })}
          </div>
        </Card>
        <Card className="p-6 space-y-4 lg:col-span-2" data-testid="payment-options">
          <div className="flex items-center gap-2"><CreditCard className="h-5 w-5 text-slate-400" /><h3 className="font-display font-semibold text-lg">Payment options</h3></div>
          <p className="text-sm text-slate-500 -mt-2">Choose which payment methods customers can use at checkout on your storefront.</p>
          <div className="grid sm:grid-cols-3 gap-3">
            {METHODS.map(([m, label, desc]) => {
              const on = !!pc.methods?.[m];
              return (
                <button key={m} onClick={() => setMethod(m, !on)} data-testid={`toggle-${m}`}
                  className={`text-left p-4 rounded-xl border transition-colors ${on ? "border-transparent bg-slate-900 text-white" : "border-slate-200 bg-white hover:bg-slate-50"}`}>
                  <div className="flex items-center justify-between"><span className="font-medium">{label}</span><span className={`h-5 w-9 rounded-full flex items-center transition-colors ${on ? "bg-emerald-400 justify-end" : "bg-slate-300 justify-start"} px-0.5`}><span className="h-4 w-4 rounded-full bg-white" /></span></div>
                  <div className={`text-xs mt-1 ${on ? "text-white/70" : "text-slate-400"}`}>{desc}</div>
                </button>
              );
            })}
          </div>
          {pc.methods?.upi && (
            <div className="pt-2 border-t" data-testid="upi-id-section">
              <Label>Your UPI ID (VPA)</Label>
              <Input value={pc.upi_id || ""} onChange={(e) => setUpiId(e.target.value)} placeholder="yourname@okhdfcbank / 98765xxxxx@ybl" className="mt-1 font-mono" data-testid="set-upi-id" />
              <p className="text-xs text-slate-400 mt-1">Customers who choose UPI will pay this exact order amount directly to this UPI ID. Verify the payment before shipping.</p>
            </div>
          )}
          <div className="pt-2 border-t">
            <div className="flex items-center gap-2">
              <h4 className="font-medium">Own payment gateway keys</h4>
              {!v.can_use_own_keys && <Badge variant="outline" className="text-amber-600 border-amber-200 bg-amber-50">Pro plan</Badge>}
            </div>
            {v.can_use_own_keys ? (
              <div className="grid sm:grid-cols-2 gap-3 mt-3">
                <div><Label>Razorpay Key ID</Label><Input value={pc.gateway?.key_id || ""} onChange={(e) => setGateway("key_id", e.target.value)} placeholder="rzp_test_…" className="font-mono" data-testid="gw-key-id" /></div>
                <div><Label>Razorpay Key Secret</Label><Input type="password" value={pc.gateway?.key_secret || ""} onChange={(e) => setGateway("key_secret", e.target.value)} placeholder="••••••••" className="font-mono" data-testid="gw-key-secret" /></div>
              </div>
            ) : <p className="text-sm text-slate-400 mt-1">Upgrade to the Pro plan to connect your own Razorpay account. Payments are handled by the platform on your current plan.</p>}
          </div>
        </Card>
      </div>
      <Button onClick={save} className="mt-6 bg-slate-900 hover:bg-slate-800" data-testid="save-settings">Save settings</Button>
    </>
  );
}
